// /driving-license/$nationality — TH per-nationality detail
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getDlNationality, DL_NATIONALITIES } from "@/data/driving-license-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Car, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, Clock, Wallet, FileText, Languages } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/driving-license/$nationality")({
  loader: ({ params }) => {
    const n = getDlNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getDlNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("ไม่พบสัญชาติ", "/driving-license/$nationality", "th")}] };
    const url = `${SITE}/driving-license/${n.slug}`;
    const title = `แลกใบขับขี่${n.nameTh} ${n.flag} เป็นใบขับขี่ไทย 5 ปี · DLT ${n.dltDays} วัน | NYC`;
    const desc = `บริการแลกใบขับขี่${n.nameTh} (${n.countryEn}) เป็นใบขับขี่ไทย 5 ปี — ${n.directConversion ? "แลกโดยตรง ไม่ต้องสอบขับ": "ต้องสอบข้อเขียน"} · ${n.needsTranslation ? "รวมแปลใบขับขี่": "ภาษาอังกฤษ ไม่ต้องแปล"} · DLT ${n.dltDays} วัน — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/driving-license/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/driving-license/$nationality", "th")},
        { name: "keywords", content: n.keywordsTh.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/driving-license/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/driving-license/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/driving-license/${n.slug}`, `/en/driving-license/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `แลกใบขับขี่${n.nameTh}เป็นใบขับขี่ไทย`,
              serviceType: "Driving License Conversion",
              description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "Audience", audienceType: n.demonymEn },
              offers: { "@type": "Offer", price: "4500", priceCurrency: "THB", description: "Translate + Embassy verify + DLT submission" },
            }),
          },
          howToSchema({
            name: `วิธีแลกใบขับขี่${n.nameTh}เป็นใบขับขี่ไทย`,
            description: `ขั้นตอนเฉพาะสำหรับใบขับขี่${n.countryEn}`,
            totalTime: `P${n.dltDays + 1}D`,
            estimatedCost: { currency: "THB", value: "4500" },
            inLanguage: "th",
            steps: [
              { name: "เตรียมใบขับขี่ต้นฉบับ", text: `ใบขับขี่${n.nameTh} (ต้องมีอายุเหลืออย่างน้อย 6 เดือน) + passport + วีซ่า/Work Permit + ใบรับรองแพทย์` },
              ...(n.needsTranslation ? [{ name: "แปลใบขับขี่", text: "นักแปลขึ้นทะเบียนกระทรวงยุติธรรม 800-1,500 บาท", url: `${SITE}/translate` }] : []),
              ...(n.embassyVerification ? [{ name: "รับรองสถานทูต", text: `นำใบขับขี่ไปสถานทูต${n.countryEn}ในกรุงเทพออก affidavit/notarial verification` }] : []),
              { name: "ยื่นที่ DLT", text: `กรมการขนส่งทางบก — ทดสอบสายตา/สี/ตอบสนอง${n.directConversion ? "" : " + สอบข้อเขียน 50 ข้อ"} · ${n.dltDays} วัน` },
              { name: "รับใบขับขี่ไทย 5 ปี", text: "smart card ออกในวันเดียวกัน 605 บาท" },
            ],
          }),
          faqPageSchema([
            { q: `คน${n.nameTh}ต้องสอบขับขี่ใหม่หรือไม่?`, a: n.directConversion ? `ไม่ต้อง — ประเทศ${n.countryEn}เป็นภาคี ${n.conventionStatus === "geneva_1949" ? "Geneva 1949" : n.conventionStatus === "vienna_1968" ? "Vienna 1968" : "bilateral reciprocity"} แลกตรง` : `ต้องสอบข้อเขียน 50 ข้อ (50% ผ่าน) แต่ไม่ต้องสอบปฏิบัติ` },
            { q: `ต้องแปลใบขับขี่หรือไม่?`, a: n.needsTranslation ? `ใช่ — ต้องแปลโดยนักแปลขึ้นทะเบียน 800-1,500 บาท` : `ไม่ต้อง — ใบขับขี่${n.countryEn}ภาษาอังกฤษอยู่แล้ว` },
            { q: `สถานทูต${n.countryEn}อยู่ที่ไหน?`, a: n.noteTh },
            { q: `ใช้เวลานานเท่าไหร่?`, a: `DLT ${n.dltDays} วัน + แปล/สถานทูต 1-2 วัน รวม 2-5 วันทำการ` },
            { q: `IDP จาก${n.countryEn}ใช้ได้หรือไม่?`, a: n.idpAccepted ? `ใช้ขับชั่วคราว 1 ปี — แต่ถ้าอยู่ไทยถาวร แนะนำแลกใบขับขี่ไทย 5 ปี` : `ไม่รองรับ — ต้องแลกใบขับขี่ไทยเท่านั้น` },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "แลกใบขับขี่", item: `${SITE}/driving-license` },
                { "@type": "ListItem", position: 3, name: `คน${n.nameTh}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: DlDetail,
});

function DlDetail() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getDlNationality(slug)!;
  const others = DL_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link><span className="mx-2">/</span>
            <Link to="/driving-license" className="hover:text-primary">แลกใบขับขี่</Link><span className="mx-2">/</span>
            <span>คน{n.nameTh}</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Car className="mr-1 inline h-3 w-3" /> Driving License · {n.countryEn}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>แลกใบขับขี่{n.nameTh} → ใบขับขี่ไทย 5 ปี
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground sm:text-lg">{n.leadTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">คน{n.nameTh}แลกใบขับขี่ไทยใช้เวลาเท่าไหร่?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              คน{n.nameTh}แลกใบขับขี่ไทย DLT 5 ปี ใช้เวลา {n.dltDays + 1}-{n.dltDays + 3} วันทำการ {n.directConversion ? "ไม่ต้องสอบขับขี่" : "ต้องสอบข้อเขียน 50 ข้อ"} {n.needsTranslation ? "· รวมแปลใบขับขี่" : "· ภาษาอังกฤษไม่ต้องแปล"} ค่าบริการ NYC 4,500 บาท + DLT 605 บาท · NYC ดำเนินการแล้ว 8,000+ เคส
            </p>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-4 max-w-3xl text-sm">
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">DLT</div><div className="font-bold">{n.dltDays} วัน</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><FileText className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">แปล</div><div className="font-bold">{n.needsTranslation ? "ต้องแปล" : "ไม่ต้อง"}</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Car className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">สอบขับ</div><div className="font-bold">{n.directConversion ? "ไม่ต้อง" : "ข้อเขียน"}</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">ค่าบริการ</div><div className="font-bold">฿4,500</div></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 space-y-12">
        <section>
          <h2 className="text-2xl font-extrabold"><Languages className="mr-2 inline h-6 w-6 text-primary" />ข้อมูลเฉพาะสำหรับคน{n.nameTh}</h2>
          <p className="mt-4 rounded-2xl border-l-4 border-primary bg-primary/5 p-4 text-sm leading-relaxed">{n.noteTh}</p>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold">ข้อมูลสำคัญแบบสรุป (Key Facts)</h2>
          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-border">
              {[
                ["สัญชาติ", `${n.flag} ${n.countryEn} (${n.demonymEn})`],
                ["Convention", n.conventionStatus === "geneva_1949" ? "Geneva 1949" : n.conventionStatus === "vienna_1968" ? "Vienna 1968" : n.conventionStatus === "both" ? "Geneva + Vienna" : "Bilateral"],
                ["แลกโดยตรง (Direct conversion)", n.directConversion ? "✅ ใช่ — ไม่ต้องสอบขับขี่" : "📝 ต้องสอบข้อเขียน"],
                ["ต้องแปลใบขับขี่", n.needsTranslation ? "ใช่ (800-1,500 บาท)" : "ไม่ต้อง (ภาษาอังกฤษ)"],
                ["รับรองสถานทูต", n.embassyVerification ? "ใช่" : "ไม่จำเป็น"],
                ["DLT Processing", `${n.dltDays} วันทำการ`],
                ["ค่าธรรมเนียม DLT", "605 บาท (smart card 5 ปี)"],
                ["ค่าบริการ NYC", "4,500 บาท (รวมทุกขั้น)"],
                ["IDP จากประเทศต้นทาง", n.idpAccepted ? "✅ ใช้ชั่วคราว 1 ปีได้" : "❌ ไม่รองรับ"],
              ].map(([k, v]) => (
                <tr key={k}><td className="py-2 pr-4 font-semibold">{k}</td><td className="py-2">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการเสริมที่มักใช้คู่กัน</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Link to="/translate" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">แปลใบขับขี่ + เอกสาร</div>
              <div className="mt-1 text-xs text-muted-foreground">ISO 17100 · นักแปลขึ้นทะเบียน MOJ</div>
            </Link>
            <Link to="/work-permit/$nationality" params={{ nationality: n.slug === "emirati" ? "japanese" : n.slug }} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Work Permit + Non-B Visa</div>
              <div className="mt-1 text-xs text-muted-foreground">วีซ่าทำงานสำหรับชาว{n.nameTh}</div>
            </Link>
            <Link to="/notary" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Notary Public Attorney</div>
              <div className="mt-1 text-xs text-muted-foreground">Affidavit, POA, รับรองเอกสาร</div>
            </Link>
            <Link to="/apostille" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Apostille (Hague)</div>
              <div className="mt-1 text-xs text-muted-foreground">รับรองเอกสารใช้ต่างประเทศ</div>
            </Link>
          </div>
        </section>

        {others.length > 0 && (
          <section className="border-t border-border pt-10">
            <h2 className="text-2xl font-extrabold">สัญชาติใกล้เคียงในภูมิภาคเดียวกัน</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/driving-license/$nationality" params={{ nationality: o.slug }} className="group rounded-2xl border border-border bg-card p-4 hover:border-primary">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{o.flag}</div>
                    <div className="font-bold">คน{o.nameTh}</div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">DLT {o.dltDays}d · {o.directConversion ? "แลกตรง" : "สอบข้อเขียน"}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดูรายละเอียด <ArrowRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
